import { EventEmitter, Injectable } from '@angular/core';
import { ApiService } from '../service/api.service';
import { SocketService } from '../service/socket.service';
import { RoleService } from '../service/role.service';
import { UserStore } from './user.store';
import { NotifyStore } from './notify.store';
import { RichiestaSedeModel } from '../interfaces/richiestaSedeModel';
import { NotifyMessageModel, NotifyType } from '../interfaces/notifyMessageModel';
import { hasHrRole } from '../constant/roles';

/**
 * Richieste di abilitazione sede: quelle dell'utente loggato (stato sulle sedi non abilitate)
 * e, per HR/HC/Admin, quelle in attesa da gestire. Si aggiorna con le notifiche via socket.
 */
@Injectable({
  providedIn: 'root'
})
export class RichiesteSedeStore {

  /** Richieste dell'utente loggato, dalla più recente. */
  public mie: RichiestaSedeModel[] = [];
  /** Richieste in attesa di gestione (valorizzato solo per HR/HC/Admin). */
  public inAttesa: RichiestaSedeModel[] = [];

  public storeUpdated: EventEmitter<void> = new EventEmitter();

  private loadedForUserId: string | null = null;
  private refreshTimer: any = null;

  constructor(
    private apiService: ApiService,
    private socketService: SocketService,
    private userStore: UserStore,
    private roleService: RoleService,
    private notifyStore: NotifyStore
  ) {
    this.socketService.receiveNotify().subscribe(() => this.scheduleRefresh());
    this.socketService.updateStoreReconnected.subscribe(() => this.scheduleRefresh());
    this.userStore.storeUpdated.subscribe(() => {
      const userId = this.currentUserId();
      if (userId && userId !== this.loadedForUserId) {
        this.scheduleRefresh();
      }
    });
    this.scheduleRefresh();
  }

  /**
   * Richieste che l'utente loggato può gestire, ricavate dalle richieste in attesa (fonte di verità) e non dalle
   * singole notifiche: un HR/HC le vede tutte finché qualcuno non le gestisce, anche se la notifica non gli era
   * stata inviata (es. profilo HR assegnato dopo la richiesta). Gli altri profili (ADMIN di riserva) vedono solo
   * quelle ricevute come notifica. Le proprie richieste sono escluse.
   */
  get daGestire(): RichiestaSedeModel[] {
    const user = this.userStore.userLogged;
    if (!user) {
      return [];
    }
    const myId = user.id || user._id;
    const isHr = hasHrRole(user.roles);
    const notificate = new Set(
      this.notifyStore.notify
        .filter(n => n.type === NotifyType.RICHIESTA_ABILITAZIONE_SEDE)
        .map(n => n.extraData?.richiestaId)
    );
    return this.inAttesa.filter(r => r.requesterId !== myId && (isHr || notificate.has(this.idOf(r))));
  }

  /** Notifiche da mostrare come tali: le richieste di abilitazione sono rappresentate da `daGestire`. */
  get notificheGeneriche(): NotifyMessageModel[] {
    return this.notifyStore.notify.filter(n => n.type !== NotifyType.RICHIESTA_ABILITAZIONE_SEDE);
  }

  /** Totale per badge e banner: notifiche generiche + richieste di abilitazione da gestire. */
  get totaleNotifiche(): number {
    return this.notificheGeneriche.length + this.daGestire.length;
  }

  /** Ultima richiesta dell'utente per la sede indicata. */
  ultimaPerSede(codiceSede: string): RichiestaSedeModel | undefined {
    return this.mie.find(r => r.codiceSede === codiceSede);
  }

  inAttesaPerSede(codiceSede: string): boolean {
    return this.ultimaPerSede(codiceSede)?.stato === 'IN_ATTESA';
  }

  async refresh(): Promise<void> {
    const userId = this.currentUserId();
    if (!userId) {
      return;
    }
    this.loadedForUserId = userId;

    try {
      this.mie = (await this.apiService.getMieRichiesteSede()) || [];
    } catch (error) {
      console.warn('RichiesteSedeStore - errore caricamento richieste utente', error);
    }

    if (this.roleService.hasHRorAdmin()) {
      try {
        this.inAttesa = (await this.apiService.getRichiesteSede('IN_ATTESA')) || [];
      } catch (error) {
        console.warn('RichiesteSedeStore - errore caricamento richieste in attesa', error);
      }
    } else {
      this.inAttesa = [];
    }

    this.storeUpdated.emit();
  }

  async richiedi(codiceSede: string, nota?: string): Promise<RichiestaSedeModel> {
    const richiesta = await this.apiService.creaRichiestaSede(codiceSede, nota);
    await this.refresh();
    return richiesta;
  }

  async annulla(richiesta: RichiestaSedeModel): Promise<void> {
    await this.apiService.annullaRichiestaSede(this.idOf(richiesta));
    await this.refresh();
  }

  async approva(richiestaId: string): Promise<RichiestaSedeModel> {
    try {
      return await this.apiService.approvaRichiestaSede(richiestaId);
    } finally {
      await this.refresh();
    }
  }

  async rifiuta(richiestaId: string, motivo: string): Promise<RichiestaSedeModel> {
    try {
      return await this.apiService.rifiutaRichiestaSede(richiestaId, motivo);
    } finally {
      await this.refresh();
    }
  }

  idOf(richiesta: RichiestaSedeModel): string {
    return richiesta.id || richiesta._id;
  }

  private currentUserId(): string | null {
    const user = this.userStore.userLogged;
    return user ? (user.id || user._id || null) : null;
  }

  /** Accorpa gli aggiornamenti ravvicinati (socket NOTIFY è inviato a tutti i client). */
  private scheduleRefresh(): void {
    if (!this.currentUserId()) {
      return;
    }
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => this.refresh(), 400);
  }
}
